<template>
  <div class="landing-wrapper">
    <div class="landing-page">
      <!-- Hero: phone image left, copy right -->
      <div class="hero-split">
        <picture class="hero-image">
          <img
            class="hero-image__img"
            src="/assets/coffee-phone-hero-1100.jpg"
            srcset="
              /assets/coffee-phone-hero-700.jpg 700w,
              /assets/coffee-phone-hero-1100.jpg 1100w,
              /assets/coffee-phone-hero-1600.jpg 1600w
            "
            sizes="(max-width: 960px) 100vw, 50vw"
            width="1600"
            height="1511"
            fetchpriority="high"
            decoding="async"
            alt="Hand holding a phone in a coffee shop, showing the Lulu Rewards card."
          />
        </picture>
        <div class="hero-content">
          <h1 class="slogan">{{ slogan }}</h1>
          <p class="subtitle">{{ subtitle }}</p>
          <div class="hero-buttons">
            <v-btn
              size="large"
              class="hero-btn hero-btn--primary"
              @click="onGetStartedClick('hero')"
            >
              Get started
            </v-btn>
            <v-btn
              size="large"
              class="hero-btn hero-btn--outline"
              @click="openHowLuluWorks"
            >
              How Lulu works
            </v-btn>
            <v-btn
              size="large"
              class="hero-btn hero-btn--outline"
              @click="requestDemo"
            >
              Request demo
            </v-btn>
          </div>
        </div>
      </div>

    </div>

    <!-- Pricing Section -->
    <div id="pricing-section" class="pricing-section">
      <v-container class="pricing-container">
        <h2 class="section-title">Pricing</h2>
        <p class="section-subtitle">Simple, transparent pricing for every stage of your business</p>
        <div class="free-trial-callout">
          <v-icon style="font-size: 18px; color: rgb(0, 109, 62); margin-right: 6px;">mdi-gift-outline</v-icon>
          30-day free trial included - no commitments, cancel anytime
        </div>
        <div class="billing-toggle">
          <v-btn-toggle
            v-model="billingPeriod"
            mandatory
            divided
            density="comfortable"
            class="billing-toggle-group"
          >
            <v-btn value="annual" class="billing-toggle-btn">Annual</v-btn>
            <v-btn value="monthly" class="billing-toggle-btn">Monthly</v-btn>
          </v-btn-toggle>
        </div>
        <div class="pricing-cards">

          <!-- Starter -->
          <v-card class="pricing-card" elevation="3">
            <v-card-text class="pricing-card-content">
              <div class="plan-name">Starter plan</div>
              <div class="plan-price">£{{ starterPrice }}<span class="plan-period">/mo per location</span></div>
              <div class="plan-billing-note">
                <div>{{ billingNote }}</div>
                <div class="plan-annual-total" :class="{ 'plan-annual-total--hidden': billingPeriod !== 'annual' }">£{{ planPricing.starter.annual }} /year per location</div>
                <div class="plan-locations-note">{{ selfServeLocationsNote }}</div>
              </div>
              <ul class="plan-features">
                <li><v-icon class="feature-icon">mdi-check</v-icon>Unlimited scans</li>
                <li><v-icon class="feature-icon">mdi-check</v-icon>Your branded iOS &amp; Android digital rewards cards</li>
                <li><v-icon class="feature-icon">mdi-check</v-icon>Redemption-ready push alerts</li>
              </ul>
              <v-btn class="plan-btn" block @click="onPlanGetStartedClick('starter')">Get started</v-btn>
            </v-card-text>
          </v-card>

          <!-- Scaler -->
          <v-card class="pricing-card" elevation="6">
            <v-card-text class="pricing-card-content">
              <div class="plan-name">Scaler plan</div>
              <div class="plan-price">£{{ scalerPrice }}<span class="plan-period">/mo per location</span></div>
              <div class="plan-billing-note">
                <div>{{ billingNote }}</div>
                <div class="plan-annual-total" :class="{ 'plan-annual-total--hidden': billingPeriod !== 'annual' }">£{{ planPricing.scaler.annual }} /year per location</div>
                <div class="plan-locations-note">{{ selfServeLocationsNote }}</div>
              </div>
              <div class="plan-inherits">Everything in Starter, plus:</div>
              <ul class="plan-features">
                <li><v-icon class="feature-icon">mdi-check</v-icon>Loyalty analytics</li>
                <li><v-icon class="feature-icon">mdi-check</v-icon>Remote stamping with automatic return-visit push alerts</li>
                <li><v-icon class="feature-icon">mdi-check</v-icon>Custom starting stamps for campaigns</li>
              </ul>
              <v-btn class="plan-btn" block @click="onPlanGetStartedClick('scaler')">Get started</v-btn>
            </v-card-text>
          </v-card>

          <!-- Established -->
          <v-card class="pricing-card" elevation="3">
            <v-card-text class="pricing-card-content">
              <div class="plan-name">Established plan</div>
              <div class="plan-price plan-price--request">Custom pricing</div>
              <div class="plan-billing-note">
                <div class="plan-locations-note">{{ establishedLocationsNote }}</div>
              </div>
              <div class="plan-inherits">Everything in Scaler, plus:</div>
              <ul class="plan-features">
                <li><v-icon class="feature-icon">mdi-check</v-icon>Dedicated support</li>
                <li><v-icon class="feature-icon">mdi-check</v-icon>Export scanning data</li>
                <li><v-icon class="feature-icon">mdi-check</v-icon>Done-for-you setup</li>
              </ul>
              <v-btn class="plan-btn" block @click="contactAboutEstablished">Contact us</v-btn>
            </v-card-text>
          </v-card>

        </div>
      </v-container>
    </div>

    <!-- Separator -->
    <div class="separator"></div>

    <!-- Contact Form Section -->
    <div id="contact-section" class="contact-section">
      <v-container class="contact-container">
        <h2 class="section-title">Get in touch</h2>
        <p class="section-subtitle">Have questions? We're here to help</p>
        <v-card class="contact-card" elevation="4">
          <v-card-text class="contact-card-content">
            <v-form ref="contactFormRef" v-model="formValid" lazy-validation>
              <v-text-field
                label="Name"
                v-model="contactForm.name"
                :rules="[rules.required]"
                required
                variant="outlined"
                color="rgb(0, 109, 62)"
              ></v-text-field>
              <v-text-field
                label="Email"
                v-model="contactForm.email"
                :rules="[rules.required, rules.email]"
                required
                variant="outlined"
                color="rgb(0, 109, 62)"
              ></v-text-field>
              <v-textarea
                label="Message"
                v-model="contactForm.message"
                :rules="[rules.required]"
                rows="4"
                required
                variant="outlined"
                color="rgb(0, 109, 62)"
              ></v-textarea>
              <v-btn
                :loading="isLoading"
                :disabled="isLoading"
                size="large"
                class="submit-btn"
                @click="submitContactForm"
              >
                Send message
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-container>
    </div>

    <!-- Footer -->
    <footer class="site-footer">
      <div class="footer-content">
        <a href="mailto:hello@lulurewards.co.uk" class="footer-email">hello@lulurewards.co.uk</a>
        <div class="footer-copyright">© 2026 Lulu Rewards</div>
        <a
          href="https://www.instagram.com/lulu_rewards?igsh=YWw0bXlxdzY4dXM3"
          target="_blank"
          rel="noopener noreferrer"
          class="footer-instagram"
          aria-label="Follow Lulu Rewards on Instagram"
        >
          <v-icon size="24">mdi-instagram</v-icon>
        </a>
      </div>
    </footer>

    <OverlayDialog
      v-model="showDialog"
      :title="dialogTitle"
      :message="dialogMessage"
      foregroundColor="rgb(0,109,62)"
      backgroundColor="white"
    />

    <!-- How Lulu Works Modal -->
    <v-dialog v-model="showHowLuluWorks" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="modal-header">
          How Lulu works
        </v-card-title>
        <v-card-text class="modal-content">
          <HowLuluWorksComponent />
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-spacer />
          <v-btn
            @click="showHowLuluWorks = false"
            class="close-modal-btn"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Terms of Service Modal -->
    <v-dialog v-model="showTermsOfService" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="modal-header">
          Terms of service
        </v-card-title>
        <v-card-text class="modal-content">
          <TermsOfServiceComponent />
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-spacer />
          <v-btn
            @click="showTermsOfService = false"
            class="close-modal-btn"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, nextTick, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import OverlayDialog from "@/components/Molecules/OverlayDialog.vue";
import HowLuluWorksComponent from "@/components/Pages/HowLuluWorksComponent.vue";
import TermsOfServiceComponent from "@/components/Pages/TermsOfServiceComponent.vue";
import { sendEmail } from "@/emailSender.js";
import { track } from "@/analytics.js";
import {
  PLAN_PRICING,
  ESTABLISHED_ENQUIRY_MESSAGE,
  SELF_SERVE_LOCATIONS_NOTE,
  ESTABLISHED_LOCATIONS_NOTE,
  monthlyEquivalent,
} from "@/utils/pricingPlans.js";

export default {
  components: {
    OverlayDialog,
    HowLuluWorksComponent,
    TermsOfServiceComponent,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    onMounted(() => {
      track("landing_page_viewed", {});
      if (route.query.signup === "true") {
        router.push("/business-signup");
      }
    });
    const businessId = ref("");
    const showDialog = ref(false);
    const dialogTitle = ref("");
    const dialogMessage = ref("");
    const formValid = ref(false);
    const isLoading = ref(false);
    const contactFormRef = ref(null);
    const contactForm = reactive({ name: "", email: "", message: "" });
    const showHowLuluWorks = ref(false);
    const showTermsOfService = ref(false);

    const billingPeriod = ref("annual");

    // The headline per-month figure follows the toggle: the monthly price,
    // or what a year works out at per month with the two free months in.
    const starterPrice = computed(() =>
      monthlyEquivalent("starter", billingPeriod.value)
    );
    const scalerPrice = computed(() =>
      monthlyEquivalent("scaler", billingPeriod.value)
    );
    const billingNote = computed(() =>
      billingPeriod.value === "annual"
        ? "Billed annually (two months free)"
        : "Billed monthly"
    );

    const slogan = "Loyalty made simple";
    const subtitle =
      "Your brand. Their mobile wallet. Every visit.";

    const rules = {
      required: (v) => !!v || "This field is required",
      email: (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
    };

    const lookUpBusiness = () => {
      if (!businessId.value || businessId.value.trim() === "") {
        showDialog.value = true;
        dialogTitle.value = "Missing business name";
        dialogMessage.value = "Please enter a business name to search";
        return;
      }
      router.push(`/business/${businessId.value}`);
    };

    const submitContactForm = async () => {
      if (!contactFormRef.value) return;
      isLoading.value = true;

      try {
        const { valid } = await contactFormRef.value.validate();
        if (!valid) {
          isLoading.value = false;
          return;
        }

        const emailData = {
          name: contactForm.name,
          email: contactForm.email,
          message: contactForm.message,
        };
        await sendEmail(emailData);

        // Reset form
        if (contactFormRef.value) {
          contactFormRef.value.reset();
          await nextTick();
          contactFormRef.value.resetValidation();
        }

        track("landing_contact_form_submitted", {});
        showDialog.value = true;
        dialogTitle.value = "Message sent! ✅";
        dialogMessage.value = "Thank you for reaching out to us.\nWe will reply to your email as soon as possible";
      } catch (error) {
        track("landing_contact_form_failed", { error_message: error?.message || "" });
        showDialog.value = true;
        dialogTitle.value = "Error sending your message ⚠️";
        dialogMessage.value = error.message;
      } finally {
        isLoading.value = false;
      }
    };

    const onGetStartedClick = (source) => {
      track("landing_get_started_clicked", { source });
      router.push("/business-signup");
    };

    const onPlanGetStartedClick = (plan) => {
      track("landing_plan_get_started_clicked", {
        plan,
        billing_period: billingPeriod.value,
      });
      router.push("/business-signup");
    };

    const openHowLuluWorks = () => {
      track("landing_how_lulu_works_opened", {});
      showHowLuluWorks.value = true;
    };

    const openTermsOfService = () => {
      track("landing_terms_of_service_opened", {});
      showTermsOfService.value = true;
    };

    const scrollToContact = () => {
      document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" });
    };

    const requestDemo = () => {
      track("landing_request_demo_clicked", {});
      contactForm.message =
        "Hi Lulu team, I'd like to request a demo of Lulu Rewards to see how it could work for my business. Please get in touch to schedule a time that works.";
      scrollToContact();
    };

    // Established is priced on request, so its CTA leads to the contact form
    // instead of self-serve sign-up.
    const contactAboutEstablished = () => {
      track("landing_plan_contact_clicked", { plan: "established" });
      contactForm.message = ESTABLISHED_ENQUIRY_MESSAGE;
      scrollToContact();
    };

    return {
      router,
      slogan,
      subtitle,
      businessId,
      lookUpBusiness,
      showDialog,
      dialogTitle,
      dialogMessage,
      formValid,
      isLoading,
      contactFormRef,
      contactForm,
      rules,
      submitContactForm,
      showHowLuluWorks,
      showTermsOfService,
      openHowLuluWorks,
      openTermsOfService,
      scrollToContact,
      requestDemo,
      onGetStartedClick,
      onPlanGetStartedClick,
      contactAboutEstablished,
      billingPeriod,
      planPricing: PLAN_PRICING,
      starterPrice,
      scalerPrice,
      selfServeLocationsNote: SELF_SERVE_LOCATIONS_NOTE,
      establishedLocationsNote: ESTABLISHED_LOCATIONS_NOTE,
      billingNote,
    };
  },
};
</script>

<style scoped>
.landing-wrapper {
  width: 100%;
  overflow-x: hidden;
  font-family: 'Roboto', sans-serif;
}

.landing-page {
  min-height: 80vh;
  background: var(--lulu-bg-gradient, linear-gradient(160deg, #f0faf5 0%, #e6f4ee 100%));
  display: flex;
  align-items: stretch;
}

.hero-split {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  min-height: 80vh;
}

.hero-image {
  display: block;
  background-color: #f8fafb;
  min-width: 0;
  overflow: hidden;
}

.hero-image__img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 64px 72px;
  max-width: 640px;
  min-width: 0;
}

.slogan {
  font-size: 60px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -1.5px;
  color: rgb(0, 109, 62);
  margin: 0 0 20px;
}

.subtitle {
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  color: rgb(0, 109, 62);
  margin: 0 0 36px;
  max-width: 520px;
  opacity: 0.85;
}

.hero-buttons {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: nowrap;
}

.hero-btn {
  padding: 12px 28px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  border-radius: 999px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.hero-btn--primary {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
}

.hero-btn--primary:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

.hero-btn--outline {
  background: transparent !important;
  color: rgb(0, 109, 62) !important;
  border: 1.5px solid rgb(0, 109, 62) !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
}

.hero-btn--outline:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

.business-lookup-section {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafb 0%, #ffffff 100%);
  padding: 60px 20px;
  scroll-margin-top: 70px;
}

.lookup-container {
  max-width: 600px;
  width: 100%;
}

.section-title {
  font-size: 42px;
  font-weight: 700;
  color: rgb(0, 109, 62);
  text-align: center;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 18px;
  color: #666;
  text-align: center;
  margin-bottom: 16px;
}

.free-trial-callout {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: rgb(0, 109, 62);
  background: rgba(0, 109, 62, 0.07);
  border: 1px solid rgba(0, 109, 62, 0.2);
  border-radius: 8px;
  padding: 10px 20px;
  margin: 0 auto 16px;
  width: fit-content;
}

.billing-toggle {
  display: flex;
  justify-content: center;
  margin: 8px auto 28px;
}

.billing-toggle-group {
  border-radius: 999px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 109, 62, 0.3);
  background: #ffffff;
}

.billing-toggle-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0 !important;
  padding: 0 22px !important;
  color: rgb(0, 109, 62) !important;
  background: #ffffff !important;
}

.billing-toggle-btn.v-btn--active {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
}

.plan-billing-note {
  font-size: 13px;
  color: #888;
  text-align: center;
  margin-top: -20px;
  margin-bottom: 24px;
  line-height: 1.4;
}

.plan-annual-total {
  font-weight: 600;
  color: rgb(0, 109, 62);
}

/* Stays in the layout on monthly so the cards keep the same height when the
   billing toggle flips. */
.plan-annual-total--hidden {
  visibility: hidden;
}

.plan-locations-note {
  margin-top: 6px;
  font-weight: 600;
  color: #444;
}

.find-btn {
  background: rgb(0, 109, 62) !important;
  color: white !important;
  padding: 14px 48px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  text-transform: none !important;
}

.find-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
}

/* Separator */
.separator {
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(0, 109, 62, 0.6) 20%, rgba(0, 109, 62, 0.6) 80%, transparent);
  margin: 0 auto;
  max-width: 80%;
}

/* Action Buttons Section */
.action-buttons-section {
  padding: 40px 20px;
  background: #ffffff;
}

.buttons-container {
  max-width: 600px;
  width: 100%;
}

.button-group {
  display: flex;
  gap: 32px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.vertical-separator {
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, transparent, rgba(0, 109, 62, 0.6) 20%, rgba(0, 109, 62, 0.6) 80%, transparent);
}

.action-btn {
  background: white !important;
  color: rgb(0, 109, 62) !important;
  padding: 14px 32px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  border-radius: 999px !important;
  transition: all 0.3s ease !important;
  text-transform: none !important;
  box-shadow: none !important;
  border: none !important;
}

.action-btn:hover {
  background: white !important;
  color: rgb(0, 109, 62) !important;
}

/* Pricing Section */
.pricing-section {
  padding: 20px 20px 80px;
  background: linear-gradient(135deg, #f8fafb 0%, #ffffff 100%);
}

.pricing-container {
  max-width: 1100px;
  width: 100%;
}

.pricing-cards {
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  padding-top: 20px;
}

.pricing-card {
  flex: 1;
  min-width: 260px;
  max-width: 320px;
  border: 2px solid rgb(0, 109, 62) !important;
  border-radius: 16px !important;
  position: relative;
  display: flex !important;
  flex-direction: column !important;
}

.pricing-card :deep(.v-card-text) {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

.pricing-card-content {
  padding: 36px 28px 28px !important;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.plan-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
  text-align: center;
}

.plan-price {
  font-size: 48px;
  font-weight: 800;
  color: rgb(0, 109, 62);
  text-align: center;
  margin-bottom: 28px;
  line-height: 1;
}

/* Padded to the same 48px height as a numeric price so the cards line up. */
.plan-price--request {
  font-size: 28px;
  font-weight: 700;
  padding: 10px 0;
}

.plan-period {
  font-size: 18px;
  font-weight: 400;
  color: #666;
}

/* Higher tiers list only what they add, so they lead with a line naming the
   plan they build on. */
.plan-inherits {
  font-size: 14px;
  font-weight: 600;
  color: rgb(0, 109, 62);
  padding-bottom: 8px;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 32px;
  flex: 1;
}

.plan-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #444;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.plan-features li:last-child {
  border-bottom: none;
}

.feature-icon {
  color: rgb(0, 109, 62) !important;
  font-size: 18px !important;
}

.plan-btn {
  background: rgb(0, 109, 62) !important;
  color: white !important;
  border: 2px solid rgb(0, 109, 62) !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  border-radius: 999px !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  height: 44px !important;
  min-height: 44px !important;
  max-height: 44px !important;
}

.plan-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

/* Contact Section */
.contact-section {
  padding: 15px 20px 60px;
  background: rgba(0, 109, 62, 0.1);
  scroll-margin-top: 70px;
}

.contact-container {
  max-width: 700px;
  width: 100%;
}

.contact-card {
  border-radius: 12px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
}

.contact-card-content {
  padding: 32px !important;
}

.submit-btn {
  background: rgb(0, 109, 62) !important;
  color: white !important;
  padding: 12px 28px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  text-transform: none !important;
  margin-top: 8px !important;
  display: block;
  margin-left: auto !important;
  margin-right: auto !important;
}

.submit-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
}

/* Footer */
.site-footer {
  background: #ffffff;
  border-top: 1px solid rgba(0, 109, 62, 0.15);
  padding: 20px 24px;
}

.footer-content {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  color: rgb(0, 109, 62);
  font-size: 14px;
  font-weight: 500;
}

.footer-email {
  color: rgb(0, 109, 62);
  text-decoration: none;
  justify-self: start;
}

.footer-email:hover {
  text-decoration: underline;
}

.footer-copyright {
  color: rgb(0, 109, 62);
  text-align: center;
  justify-self: center;
}

.footer-instagram {
  color: rgb(0, 109, 62);
  justify-self: end;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.footer-instagram:hover {
  transform: translateY(-1px);
  opacity: 0.8;
}

@media (max-width: 600px) {
  .footer-content {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 10px;
  }

  .footer-email,
  .footer-copyright,
  .footer-instagram {
    justify-self: center;
  }
}

/* Modal Styles */
.modal-header {
  background: linear-gradient(135deg, rgb(0, 109, 62) 0%, rgb(0, 85, 48) 100%) !important;
  color: white !important;
  font-weight: 700 !important;
  font-size: 20px !important;
  padding: 20px !important;
  text-align: center !important;
}

.modal-content {
  padding: 24px !important;
  max-height: 500px;
  overflow-y: auto;
}

.modal-content :deep(.v-img) {
  display: none !important;
}

.modal-content :deep(.text-container) {
  display: none !important;
}

.modal-content :deep(.content) {
  max-width: 100% !important;
  padding: 0 !important;
}

.modal-actions {
  padding: 16px 24px !important;
  background-color: #f5f7fa;
  border-top: 1px solid #e2e8f0;
}

.close-modal-btn {
  background: rgb(0, 109, 62) !important;
  color: white !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-radius: 999px !important;
  padding: 8px 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.close-modal-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px) !important;
}

@media (max-width: 960px) {
  .hero-split {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .hero-image {
    width: 100%;
    aspect-ratio: 4 / 3;
    min-height: 0;
  }

  .hero-content {
    padding: 40px 32px 48px;
    max-width: 100%;
    align-items: center;
    text-align: center;
  }

  .subtitle {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-buttons {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .slogan {
    font-size: 36px;
    margin-bottom: 14px;
    letter-spacing: -1px;
  }

  .subtitle {
    font-size: 17px;
    margin-bottom: 26px;
  }

  .hero-buttons {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    max-width: 320px;
  }

  .hero-btn {
    padding: 11px 24px !important;
    font-size: 15px !important;
    width: 100%;
  }

  .hero-image {
    aspect-ratio: 4 / 3;
    min-height: 0;
  }

  .section-title {
    font-size: 32px;
  }

  .section-subtitle {
    font-size: 16px;
  }

  .button-group {
    flex-direction: column;
    width: 100%;
  }

  .action-btn {
    width: 100%;
  }

  .contact-card-content {
    padding: 24px !important;
  }
}

@media (max-width: 960px) {
  .pricing-cards {
    flex-direction: column;
    align-items: center;
  }

  .pricing-card {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .slogan {
    font-size: 30px;
  }

  .subtitle {
    font-size: 16px;
  }

  .hero-btn {
    padding: 10px 22px !important;
    font-size: 14px !important;
  }

  .hero-image {
    aspect-ratio: 4 / 3;
    min-height: 0;
  }

  .hero-content {
    padding: 32px 20px 40px;
  }

  .section-title {
    font-size: 28px;
  }

  .section-subtitle {
    font-size: 14px;
  }
}
</style>
